import { Icon, Stack, Typography } from '@mui/material';
import { CalendarIcon, DoneCircleIcon, GroupsIcon, StudentIcon } from '@schooly/style';
import React, { FC } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import {
  isSessionTab,
  RegisterGroupIds,
  RegisterPeriod,
  RegisterPeriodStructure,
  RegisterTab,
  RosterGroup,
} from './types';
import {
  getFilledStructureCount,
  getPerPeriodStructureCoverage,
  getStudentCountForSession,
  StructurePeriodCoverage,
} from './utils/registerCounts';
import { getGroupsForTab } from './utils/registerGroupIds';
import { getPeriodOrderLabel, getRegisterTabMessageId } from './utils/registerLabels';

type RegisterTabCounter = { icon: React.ReactNode; label: string; key: string };
type RegisterTabLabelProps = Omit<RegisterTabCounter, 'key'>;

const RegisterTabLabel: FC<RegisterTabLabelProps> = ({ icon, label }) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Icon sx={(theme) => ({ fontSize: theme.spacing(1.5), color: 'inherit' })}>{icon}</Icon>
    <Typography color="inherit">{label}</Typography>
  </Stack>
);

const RegisterTabCounters: FC<{ counters: RegisterTabCounter[] }> = ({ counters }) => (
  <Stack direction="row" gap={1} mt={0.25}>
    {counters.map((counter) => (
      <RegisterTabLabel key={counter.key} icon={counter.icon} label={counter.label} />
    ))}
  </Stack>
);

type SessionTabTooltipProps = {
  scopeLabel: string;
  groupCount: number;
  studentCount: number;
};

const SessionTabTooltip: FC<SessionTabTooltipProps> = ({
  scopeLabel,
  groupCount,
  studentCount,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={0.5}>
      <Typography>
        {formatMessage({ id: 'attendance-RegisterTabCoverage' }, { scope: scopeLabel })}
      </Typography>
      <RegisterTabLabel
        icon={<GroupsIcon />}
        label={formatMessage({ id: 'attendance-RegisterGroupsCount' }, { count: groupCount })}
      />
      <RegisterTabLabel
        icon={<StudentIcon />}
        label={formatMessage({ id: 'attendance-RegisterStudentsCount' }, { count: studentCount })}
      />
    </Stack>
  );
};

const PeriodTabTooltip: FC<{
  scopeLabel: string;
  structures: StructurePeriodCoverage[];
}> = ({ scopeLabel, structures }) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={0.5}>
      <Typography>
        {formatMessage({ id: 'attendance-RegisterTabCoverage' }, { scope: scopeLabel })}
      </Typography>
      {structures.map(({ structure, periods }) => {
        const structureEmpty = periods.every((period) => period.groupCount === 0);

        return (
          <Stack
            key={structure.id}
            gap={0.5}
            pl={0.5}
            color={structureEmpty ? 'common.grey' : undefined}
          >
            <RegisterTabLabel icon={<CalendarIcon />} label={structure.name} />
            <Stack gap={0.25}>
              {periods.map((period) => (
                <Stack
                  key={period.id}
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  pl={2}
                  color={period.groupCount === 0 ? 'common.grey' : undefined}
                >
                  <Typography color="inherit">
                    {getPeriodOrderLabel(period, formatMessage)}:
                  </Typography>
                  <RegisterTabLabel
                    icon={<GroupsIcon />}
                    label={formatMessage(
                      { id: 'attendance-RegisterGroupsCount' },
                      { count: period.groupCount },
                    )}
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

type GetRegisterTabItemsParams = {
  availableTabs: RegisterTab[];
  addedGroups: RosterGroup[];
  groupIds: RegisterGroupIds;
  timetableStructures: RegisterPeriodStructure[];
  periodsByStructureId: Record<string, RegisterPeriod[]>;
  formatMessage: IntlShape['formatMessage'];
};

export const getRegisterTabItems = ({
  availableTabs,
  addedGroups,
  groupIds,
  timetableStructures,
  periodsByStructureId,
  formatMessage,
}: GetRegisterTabItemsParams) =>
  availableTabs.map((tab) => {
    const labelId = getRegisterTabMessageId(tab);
    const added = getGroupsForTab(addedGroups, groupIds, tab);
    const isSession = isSessionTab(tab);
    const counters: RegisterTabCounter[] = isSession
      ? [
          { key: 'groups', icon: <GroupsIcon />, label: `${added.length}` },
          {
            key: 'students',
            icon: <StudentIcon />,
            label: `${getStudentCountForSession(added, tab)}`,
          },
        ]
      : [
          {
            key: 'structures',
            icon: <DoneCircleIcon />,
            label: `${getFilledStructureCount(timetableStructures, periodsByStructureId, groupIds.periods)}/${timetableStructures.length}`,
          },
        ];
    const scopeLabel = formatMessage({ id: labelId });

    return {
      value: tab,
      labelId,
      tooltipContent: isSession ? (
        <SessionTabTooltip
          scopeLabel={scopeLabel}
          groupCount={added.length}
          studentCount={getStudentCountForSession(added, tab)}
        />
      ) : (
        <PeriodTabTooltip
          scopeLabel={scopeLabel}
          structures={getPerPeriodStructureCoverage(
            timetableStructures,
            periodsByStructureId,
            groupIds.periods,
          )}
        />
      ),
      label: (
        <Stack alignItems="flex-start">
          <Typography
            variant="h2"
            sx={{ '.MuiTab-root:not(.Mui-selected) &': { color: 'common.grey' } }}
          >
            {formatMessage({ id: labelId })}
          </Typography>
          <RegisterTabCounters counters={counters} />
        </Stack>
      ),
    };
  });
